import { useState } from "react";
import { useLocalization } from "@fluent/react";
import { Button } from "@narmi/design_system";

import type { ReactNode } from "react";

// two rows of four: seven cards plus the "+ x more" tile
const COLLAPSED_CARD_COUNT = 7;

interface RecipientsGridProps<T> {
  items: T[];
  renderItem: (item: T) => ReactNode;
  // shown in place of the grid when there are no items
  emptyMessage: string;
  // only the consumer recipient redesign collapses, legacy grids show every card
  isCollapsible?: boolean;
}

// grid view for the recipient sections: collapses to two rows and expands to
// show every card
const RecipientsGrid = <T,>({
  items,
  renderItem,
  emptyMessage,
  isCollapsible = false,
}: RecipientsGridProps<T>) => {
  const { l10n } = useLocalization();
  const [isExpanded, setIsExpanded] = useState(false);

  // eight or more cards collapse to seven plus the "+ x more" tile
  const shouldCollapse = isCollapsible && items.length > COLLAPSED_CARD_COUNT;
  const visibleItems =
    shouldCollapse && !isExpanded
      ? items.slice(0, COLLAPSED_CARD_COUNT)
      : items;
  const hiddenCount = items.length - COLLAPSED_CARD_COUNT;

  if (!items.length) {
    return (
      <div className="no-recipients">
        <div>{emptyMessage}</div>
      </div>
    );
  }

  return (
    <div className="recipients-container">
      {visibleItems.map(renderItem)}
      {shouldCollapse ? (
        // always the last tile, after every card. It stays mounted between
        // states so the button keeps focus
        <div key="recipients-grid-toggle" className="recipients-grid-toggle">
          <Button
            kind="plain"
            size="m"
            startIcon={isExpanded ? undefined : "plus"}
            label={
              isExpanded
                ? l10n.getString("recipients-grid-show-less")
                : l10n.getString("recipients-grid-show-more", {
                    count: hiddenCount,
                  })
            }
            aria-expanded={isExpanded}
            onClick={() => setIsExpanded(!isExpanded)}
          />
        </div>
      ) : null}
    </div>
  );
};

export default RecipientsGrid;
